import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ExpenseJournalSearchAndFilter from '../ExpenseJournalSearchAndFilter';
import ExpenseJournalEntriesTable from '../ExpenseJournalEntriesTable';
import { getExpenseJournalListAction } from 'redux/action/expenseJournalAction';
import { clearList, resetJournalList } from 'redux/slice/expenseJournalSlice';
import { AppDispatch } from 'redux/store';
import styles from './index.module.scss';
import { ExpenseJournalStatus } from 'constants/Data';
import { useDebounce } from 'use-debounce';
interface RowData {
	id: string;
	date: string;
	dateRange: string;
	qboJournalNo: string;
	qboLink?: string;
	amount: number;
	publishedBy: string;
	notes: string;
}

interface ExpenseJournalMainProps {
	selectedExpenseGroup: string;
	onExpenseGroupChange: (expenseGroupId: string) => void;
	expenseGroupOptions: Array<{ value: string; label: string }>;
	expenseGroupsLoading: boolean;
	handlePayPeriodId?: (value: string) => void;
	searchValue?: string;
	setSearchValue?: (v: string) => void;
	filterValue?: any;
	setFilterValue?: (v: any) => void;
	sortBy?: string | null;
	setSortBy?: (v: string | null) => void;
	sortType?: string | null;
	setSortType?: (v: string | null) => void;
	isExpenseAllocation?: string;
	onScreenChange?: () => void;
	isExpanded?: boolean;
}

export default function ExpenseJournalMain(props: ExpenseJournalMainProps) {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { selectedExpenseGroup, expenseGroupOptions } = props;
	const dispatch = useDispatch<AppDispatch>();

	// Redux state
	const {
		listData,
		listLoading,
		listCount,
		listPagination,
		isLoadingMore,
		hasMore,
		firstTimeFetchLoading,
	} = useSelector((state: any) => state.expenseJournal);
	const { selectedCompanyDetails } = useSelector(
		(state: any) => state.companies
	);

	// Filter state
	const currentYear = new Date().getFullYear();
	const [year, setYear] = useState<number | null>(currentYear);
	const [status, setStatus] = useState<'All' | ExpenseJournalStatus>('All');
	const [search, setSearch] = useState<string>('');
	const [debounceSearchValue] = useDebounce(search, 1000);
	//eslint-disable-next-line
	const [page, setPage] = useState<number>(1);
	const [sortBy, setSortBy] = useState<string | null>('date');
	const [sortType, setSortType] = useState<string | null>('desc');

	// Fetch data function with lazy loading support
	const fetchJournalEntries = useCallback(
		async (pageNum = 1, sortByParam = sortBy, sortTypeParam = sortType) => {
			const params: any = {
				page: pageNum,
				limit: 10,
				search: debounceSearchValue,
				status: status === 'All' ? '' : status,
			};
			if (year !== null) {
				params.year = year;
			}

			if (sortByParam) {
				params.sort = sortByParam;
			}
			if (sortTypeParam) {
				params.sortType = sortTypeParam;
			}

			dispatch(getExpenseJournalListAction(params));
		},
		[dispatch, debounceSearchValue, status, year, sortBy, sortType]
	);

	// Load more entries for infinite scroll
	const handleLoadMore = () => {
		if (hasMore && !isLoadingMore && !listLoading) {
			const nextPage = listPagination.currentPage + 1;
			fetchJournalEntries(nextPage, sortBy, sortType);
		}
	};

	// Handle sorting
	const handleSort = (newSortBy: string, newSortType: string) => {
		setSortBy(newSortBy);
		setSortType(newSortType);
		setPage(1);
		// Reset data and fetch first page when sorting changes
		dispatch(resetJournalList());
		fetchJournalEntries(1, newSortBy, newSortType);
	};

	// Handle company changes - reload all data when company changes
	useEffect(() => {
		if (selectedCompanyDetails) {
			// Reset all filter states when company changes
			setYear(currentYear);
			setStatus('All');
			setSearch('');
			setPage(1);
			setSortBy('date');
			setSortType('desc');

			// Reset Redux state
			dispatch(resetJournalList());
		}
	}, [selectedCompanyDetails, dispatch, currentYear]);

	// Fetch data when filters change (reset to page 1)
	useEffect(() => {
		if (!selectedCompanyDetails) return;
		// Reset data and fetch first page when filters change
		dispatch(resetJournalList());
		fetchJournalEntries(1, sortBy, sortType);
	}, [
		selectedCompanyDetails,
		debounceSearchValue,
		status,
		year,
		sortBy,
		sortType,
		fetchJournalEntries,
		dispatch,
	]);

	// Clear data when component unmounts
	useEffect(() => {
		return () => {
			dispatch(clearList());
		};
	}, [dispatch]);

	// Transform data to match expected format
	const transformedData: RowData[] = useMemo(() => {
		return listData.map((item: any) => ({
			id: item.id,
			date: item.date,
			dateRange: item.dateRange,
			qboJournalNo: item.qboJournalNo,
			qboLink: item.qboJournalTrnId,
			amount: parseFloat(item.amount),
			publishedBy:
				`${item.createdBy?.firstName || ''} ${item.createdBy?.lastName || ''
					}`.trim() ||
				item.createdBy?.email ||
				'Unknown',
			notes: item.notes,
			status: item.status === 1 ? 'Published' : 'Draft',
			expenseGroupId: item.expenseGroupId,
			mode: item.mode, // Include mode field from backend
		}));
	}, [listData]);

	// Handle table changes (sorting, pagination)
	const handleTableChange = (_: any, __: any, sorter: any) => {
		if (sorter.columnKey && sorter.order) {
			handleSort(sorter.columnKey, sorter.order === 'ascend' ? 'asc' : 'desc');
		}
	};

	return (
		<div className={styles['journal-entries-container']}>
			<ExpenseJournalSearchAndFilter
				year={year}
				onYearChange={setYear}
				onStatusChange={setStatus}
				search={search}
				onSearchChange={setSearch}
				status={status}
			/>

			<div className={styles['journal-entries-container__table']}>
				<ExpenseJournalEntriesTable
					data={transformedData}
					total={listCount}
					currentPage={listPagination.currentPage}
					onPageChange={setPage}
					onTableChange={handleTableChange}
					loading={listLoading || firstTimeFetchLoading || isLoadingMore}
					hasMore={hasMore}
					isLoadingMore={isLoadingMore}
					onLoadMore={handleLoadMore}
					year={year ?? undefined}
					status={status}
					search={search}
					expenseGroupOptions={expenseGroupOptions}
				/>
			</div>
		</div>
	);
}
